import type { IMenuOption } from '@/composition/use-menu';
import { sortedSizes } from '@/domain/catalog/catalog-filters';
import { capitalize } from '@/domain/catalog/catalog-status-text';
import type { TTranslate } from '@/domain/catalog/catalog-status-text';
import { groupCatalog, menuIdOf } from '@/domain/catalog/catalog-view';
import { menuChoiceName, menuChoices } from '@/domain/catalog/menu-choices';
import { groupRuleText, optionUsage } from '@/domain/catalog/option-groups';
import type { ICategory } from '@/interfaces/ICategory';
import type { IItem } from '@/interfaces/IItem';
import type { IMenu } from '@/interfaces/IMenu';
import type { IOption } from '@/interfaces/IOption';
import type { ICardTextParams } from '../cards/card-text';
import { dateStrInTimeZone } from '../gpthelp-business-day';
import { resolveOfferedMenus } from '../menu/gpthelp-admin-catalog';
import {
  allItemOptions,
  liveOptionItems,
} from '../menu/gpthelp-admin-menu-local';
import { resolveItemVisibility } from '../menu/gpthelp-admin-menu-visibility';
import type { IGpthelpVisibilityCtx } from '../menu/gpthelp-admin-menu-visibility';
import type { IGpthelpHostI18n } from '../tools/tool-types';
import type {
  IGpthelpExcelColumn,
  IGpthelpExcelSheetInput,
} from './gpthelp-excel-builder';

/**
 * The menu as an Excel file (plan P1-7), pure: a products sheet in Menú
 * order, one row per product or per size, and an options sheet with the
 * groups those products use, one row per choice. Every text is in the admin
 * language; the availability column names the branch, because the file
 * travels outside the panel.
 */

export interface ICatalogExportInput {
  categories: ICategory[];
  items: IItem[];
  /** The menus order types use (`menuStore.availableMenus`); null while they load. */
  availableMenus: IMenuOption[] | null;
  /**
   * The store's menus other than the default one, paused ones included
   * (`GET /menus`, the list the Menú page names its menus from); empty when
   * the file has no menu column (`catalogExportNeedsMenuNames`).
   */
  storeMenus: IMenu[];
  ctx: IGpthelpVisibilityCtx;
  /** The current branch: «Disponible en {branch}». */
  branchName: string;
  /** Only this menu; null = every menu. */
  menuId: number | null;
  /** Only these categories; null = every category. */
  categoryIds: number[] | null;
  includeOptions: boolean;
  /** The branch time zone: a price change is dated as that day there. */
  timeZone: string;
  t: TTranslate;
}

interface IMenuInOrder {
  id: number | null;
  /** Some order type uses it. */
  offered: boolean;
}

interface IExportMenu extends IMenuInOrder {
  /** The menu's name; null when the store has none for it (`menuTitles`). */
  title: string | null;
}

type TRow = Record<string, string | number | null>;

const moneyOf = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const nullableMoneyOf = (value: unknown): number | null =>
  value === null || value === undefined ? null : moneyOf(value);

const textOrNull = (value: string | null | undefined): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

/** The day of `value` in `timeZone` (yyyy-MM-dd); null when it is not a date. */
function dayOf(
  value: Date | string | null | undefined,
  timeZone: string
): string | null {
  if (value === null || value === undefined) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : dateStrInTimeZone(date, timeZone);
}

function byIndexThenId(a: ICategory, b: ICategory): number {
  return (a.index ?? 0) - (b.index ?? 0) || a.id - b.id;
}

/**
 * The store's menus in catalog order: the offered ones, then any other menu
 * a category belongs to, in the order of its first category (the menu
 * tools' order).
 */
function menusInOrder(
  categories: readonly ICategory[],
  availableMenus: IMenuOption[] | null
): IMenuInOrder[] {
  const menus: IMenuInOrder[] = resolveOfferedMenus(
    availableMenus
  ).map((menu) => ({ id: menu.id ?? null, offered: true }));
  const known = new Set(menus.map((menu) => menu.id));
  for (const category of [...categories].sort(byIndexThenId)) {
    const id = menuIdOf(category);
    if (known.has(id)) continue;
    known.add(id);
    menus.push({ id, offered: false });
  }
  return menus;
}

/**
 * The products sheet has the menu column: the store has several menus. Ids
 * are unique, so one of them is a menu other than the default one.
 */
const hasMenuColumn = (menus: readonly IMenuInOrder[]): boolean =>
  menus.length > 1;

/**
 * The file has the menu column, so it needs the store's menu list
 * (`storeMenus`) to name the menus in it.
 */
export function catalogExportNeedsMenuNames(
  categories: readonly ICategory[],
  availableMenus: IMenuOption[] | null
): boolean {
  return hasMenuColumn(menusInOrder(categories, availableMenus));
}

/**
 * Each menu's name as the Menú page shows it (V2CatalogMenuSwitcher
 * `nameOf`): «Menú principal» for the default menu, then each live menu's
 * name, or «Menú {n}» by its place in the page's menu list. A paused menu
 * (not in that list) keeps its own name; null when the store has no name
 * for it (a deleted menu, or a paused one without a name).
 */
function menuTitles(
  storeMenus: readonly IMenu[],
  t: TTranslate
): (id: number | null) => string | null {
  const choices = menuChoices(
    storeMenus.filter((menu) => menu.active),
    null
  );
  return (id) => {
    if (id === null) return t('menuPage.menus.main');
    const name = menuChoiceName(choices, id);
    if (name.kind === 'named') return name.name;
    if (name.kind === 'unnamed')
      return t('menuPage.menus.unnamed', { n: name.position });
    // Not in the Menú page's list: a paused or a deleted menu.
    return textOrNull(storeMenus.find((menu) => menu.id === id)?.name);
  };
}

/** The products sheet's rows and the exported products, in Menú order. */
function productRows(
  input: ICatalogExportInput,
  menus: IExportMenu[],
  yesNo: (value: boolean) => string
): { rows: TRow[]; items: IItem[] } {
  const categoryFilter =
    input.categoryIds !== null ? new Set(input.categoryIds) : null;
  const rows: TRow[] = [];
  const items: IItem[] = [];

  for (const menu of menus) {
    if (input.menuId !== null && menu.id !== input.menuId) continue;
    for (const section of groupCatalog(
      input.categories,
      input.items,
      menu.id
    )) {
      if (categoryFilter && !categoryFilter.has(section.category.id)) continue;
      for (const item of section.items) {
        const visibility = resolveItemVisibility({
          item,
          category: section.category,
          menuOffered: menu.offered,
          ctx: input.ctx,
        });
        // The Menú page's switch: on, and not hidden in this branch.
        const availableHere =
          !visibility.hiddenReasons.includes('item_disabled') &&
          !visibility.hiddenReasons.includes('item_hidden_in_branch');
        const product = {
          menu: menu.title,
          category: section.category.name ?? '',
          product: item.name ?? '',
          description: textOrNull(item.description),
          lastPriceChange: dayOf(item.lastPriceChangeAt, input.timeZone),
          id: item.id,
        };

        const sizes = sortedSizes(item);
        if (sizes.length === 0)
          rows.push({
            ...product,
            size: null,
            price: moneyOf(item.price),
            discountedPrice: nullableMoneyOf(item.discountedPrice),
            availableIn: yesNo(availableHere),
            visibleNow: yesNo(visibility.customerVisibleNow),
          });
        // A paused size can be neither ordered nor sold.
        for (const size of sizes)
          rows.push({
            ...product,
            size: size.title ?? '',
            price: moneyOf(size.cost),
            discountedPrice: nullableMoneyOf(size.discountedCost),
            availableIn: yesNo(availableHere && size.active),
            visibleNow: yesNo(visibility.customerVisibleNow && size.active),
          });
        items.push(item);
      }
    }
  }
  return { rows, items };
}

/**
 * The options sheet's rows: each group the exported products link, in the
 * order they first appear, one row per choice (paused ones included).
 */
function optionRows(
  input: ICatalogExportInput,
  exported: IItem[],
  yesNo: (value: boolean) => string
): TRow[] {
  const groups = new Map<number, IOption>();
  for (const item of exported)
    for (const { option, link } of allItemOptions(item))
      if (
        link !== null &&
        typeof option.id === 'number' &&
        !groups.has(option.id)
      )
        groups.set(option.id, option);

  // How many products of the store use each group (the Opciones count).
  const usage = optionUsage(input.items);
  const rows: TRow[] = [];
  for (const [id, group] of groups) {
    // The editor's own wording (V2CatalogGroupPickerDialog `ruleOf`).
    const rule = groupRuleText({
      type: group.type,
      multiple: group.multiple,
      mandatory: group.mandatory,
      min: group.min ?? null,
      max: group.max ?? null,
    });
    const shared = {
      group: group.title ?? '',
      rule: capitalize(input.t(rule.key, rule.params)),
      usedBy: usage[id]?.length ?? 0,
    };
    const choices = liveOptionItems(group);
    if (choices.length === 0) rows.push(shared);
    for (const choice of choices)
      rows.push({
        ...shared,
        choice: choice.title ?? '',
        price: moneyOf(choice.cost),
        discountedPrice: nullableMoneyOf(choice.discountedCost),
        paused: yesNo(!choice.active),
      });
  }
  return rows;
}

/**
 * The sheets of the menu export: products first, then the options when
 * `includeOptions`. The menu column only in stores with several menus.
 */
export function catalogExportSheets(
  input: ICatalogExportInput
): IGpthelpExcelSheetInput[] {
  const { t } = input;
  const yesNo = (value: boolean) =>
    value ? t('assistant.export.yes') : t('assistant.export.no');
  const titleOf = menuTitles(input.storeMenus, t);
  const menus: IExportMenu[] = menusInOrder(
    input.categories,
    input.availableMenus
  ).map((menu) => ({ ...menu, title: titleOf(menu.id) }));
  const products = productRows(input, menus, yesNo);

  const productColumns: IGpthelpExcelColumn[] = [
    ...(hasMenuColumn(menus)
      ? [{ key: 'menu', header: t('assistant.export.col.menu') }]
      : []),
    { key: 'category', header: t('assistant.export.col.category') },
    { key: 'product', header: t('assistant.export.col.product') },
    { key: 'size', header: t('assistant.export.col.size') },
    { key: 'price', header: t('assistant.export.col.price'), type: 'number' },
    {
      key: 'discountedPrice',
      header: t('assistant.export.col.discountedPrice'),
      type: 'number',
    },
    {
      key: 'availableIn',
      header: t('assistant.export.col.availableIn', {
        branch: input.branchName,
      }),
    },
    { key: 'visibleNow', header: t('assistant.export.col.visibleNow') },
    { key: 'description', header: t('assistant.export.col.description') },
    {
      key: 'lastPriceChange',
      header: t('assistant.export.col.lastPriceChange'),
      type: 'date',
    },
    { key: 'id', header: t('assistant.export.col.id'), type: 'number' },
  ];
  const sheets: IGpthelpExcelSheetInput[] = [
    {
      name: t('assistant.export.sheet.products'),
      columns: productColumns,
      rows: products.rows,
    },
  ];
  if (!input.includeOptions) return sheets;

  sheets.push({
    name: t('assistant.export.sheet.options'),
    columns: [
      { key: 'group', header: t('assistant.export.col.group') },
      { key: 'rule', header: t('assistant.export.col.rule') },
      { key: 'choice', header: t('assistant.export.col.choice') },
      { key: 'price', header: t('assistant.export.col.price'), type: 'number' },
      {
        key: 'discountedPrice',
        header: t('assistant.export.col.discountedPrice'),
        type: 'number',
      },
      { key: 'paused', header: t('assistant.export.col.paused') },
      {
        key: 'usedBy',
        header: t('assistant.export.col.usedBy'),
        type: 'number',
      },
    ],
    rows: optionRows(input, products.items, yesNo),
  });
  return sheets;
}

/** «lodemarta-menu-2026-10-04.xlsx»: the store, the word «menu» and today in the branch. */
export function catalogExportFilename(input: {
  slug: string;
  t: TTranslate;
  now: Date;
  timeZone: string;
}): string {
  return `${[
    input.slug,
    input.t('assistant.export.file.menu'),
    dateStrInTimeZone(input.now, input.timeZone),
  ]
    .filter((part) => part !== '')
    .join('-')}.xlsx`;
}

/**
 * vue-i18n's `$t` in the admin language for the text of a file: plain, so
 * no card bidi isolate ends up in a cell.
 */
export function fileTranslator(i18n: IGpthelpHostI18n): TTranslate {
  return (key, params = {}) => {
    const values: ICardTextParams = {};
    for (const [name, value] of Object.entries(params))
      values[name] = typeof value === 'number' ? value : String(value);
    const text = i18n.t(key, i18n.locale, values);
    return typeof text === 'string' ? text : key;
  };
}
